import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithPopup,
  signInWithRedirect, 
  signOut
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';

const isTelegramWebApp = (): boolean => {
  try {
    // Primary check: Look for Telegram WebApp object and its key properties
    // @ts-expect-error: Telegram WebApp object may not exist in all environments
    const webApp = window.Telegram?.WebApp;
    if (webApp) {
      // Verify it's a real Telegram WebApp by checking essential properties
      return typeof webApp.initData === 'string' && 
             typeof webApp.initDataUnsafe === 'object' &&
             typeof webApp.platform === 'string';
    }

    // Fallback: Check user agent for non-Safari Telegram clients
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.includes('telegram') || ua.includes('webview') ; // to test
  } catch {
    // If accessing window.Telegram throws, we're not in Telegram
    return false;
  }
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async (): Promise<void> => {
    try {
      if (isTelegramWebApp()) {
        // For Telegram WebApp, use redirect-based auth
        await signInWithRedirect(auth, googleProvider);
        // No need to setUser here as onAuthStateChanged will handle it after redirect
      } else {
        // For non-Telegram environments, use popup
        const result = await signInWithPopup(auth, googleProvider);
        if (result?.user) {
          setUser(result.user);
        }
      }
    } catch (error: unknown) {
      console.error('Error signing in with Google:', error);
      
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('An unknown error occurred during Google sign in');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
    } catch (error: unknown) {
      console.error('Error signing out:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred during logout');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
